import {useTranslation} from '@/translations/i18n'
import {useController, useFormContext} from 'react-hook-form'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {z} from 'zod'
import {InputSelectUser, UserSmallSelection} from '@/features/users/components/input-select-user/InputSelectUser'
import {useMemo} from 'react'
import {TeamGroup, TeamSpecializationGroup} from '@/features/treatment-requests/teams/types'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'

interface EditProjectTeamOperatorFieldProps {
    index: number
    disabledOptions?: string[]
}

const EditProjectTeamOperatorSchema = ProjectFieldsSchema.pick({
    team: true
})
type EditProjectTeamOperatorSchema = z.infer<typeof EditProjectTeamOperatorSchema>

/**
 * Component for editing team operator fields in project forms
 * Handles user selection with specialization-based filtering
 */
export const EditProjectTeamOperatorField = ({index, disabledOptions}: EditProjectTeamOperatorFieldProps) => {
    const {t} = useTranslation()

    // Fetch all user specializations for filtering
    const {data: specializations} = useGetUserSpecializations({limit: 100})

    const {control} = useFormContext<EditProjectTeamOperatorSchema>()
    const {field} = useController({
        control,
        name: `team.${index}`
    })

    /**
     * Handles team member selection changes
     * Updates the field value with selected user data or clears it if null
     */
    const handleChangeTeamMember = (value: UserSmallSelection | null) => {
        if (value) {
            field.onChange({
                ...field.value,
                id: value.id,
                fullName: value.fullName,
                specializationName: value.specializationName
            })
        } else {
            field.onChange({
                ...field.value,
                id: '',
                fullName: '',
                specializationName: ''
            })
        }
    }

    /**
     * Maps referring doctor specializations to their IDs
     * Includes Neurologo, Fisiatra, and Neuropsichiatra infantile
     */
    const referringDoctorSpecializationIds = useMemo(() => {
        return specializations?.pages[0]?.data
            .filter(
                specialization =>
                    specialization.name == 'Neurologo' ||
                    specialization.name == 'Fisiatra' ||
                    specialization.name == 'Neuropsichiatra infantile'
            )
            ?.map(specialization => specialization.id)
    }, [specializations])

    /**
     * Maps team group to specialization group for filtering
     * Determines which type of specialists to show based on team role
     */
    const specializationGroup = useMemo(() => {
        const mapGroupToSpecializationGroup = {
            referring_doctor: 'referring_doctor',
            therapist: 'therapist',
            almoner: 'psychosocial_staff',
            psychologist: 'psychologist',
            doctor: 'doctor',
            other: 'other'
        } as const satisfies Record<TeamGroup, TeamSpecializationGroup>

        return mapGroupToSpecializationGroup[field.value.group]
    }, [field.value.group, specializations])

    /**
     * Creates filters for user selection based on specialization group
     * Uses different filtering strategies for referring doctors vs other roles
     */
    const filters = useMemo(() => {
        if (specializationGroup == 'referring_doctor') {
            // For referring doctors, filter by specific specialization IDs
            return {
                status: 'operative',
                specializationId: referringDoctorSpecializationIds
            }
        }

        // For other roles, filter by specialization group
        return {
            status: 'operative',
            specializationGroup: specializationGroup
        }
    }, [specializationGroup, referringDoctorSpecializationIds])

    return (
        <InputSelectUser
            label={t('projects:consultant_specialist')}
            placeholder={t('commons:select')}
            value={field.value}
            onChange={handleChangeTeamMember}
            disabledOptions={disabledOptions}
            filters={filters}
        />
    )
}
